import { FathomEvent } from "@lib/constants";
import { useApplicationTranslation } from "@lib/hooks/translations";
import useLocale from "@lib/hooks/useLocale";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import useTrackEvent from "@lib/hooks/useTrackEvent";
import { ApplicationFormValues } from "@lib/mapApplicationData/types";
import { applicationTypeToPath, getApplicationProcessPath } from "@lib/routing";
import FormScreen from "@modules/application/components/FormScreen";
import useApplicationContext from "@modules/application/hooks/useApplicationContext";
import useSteps from "@modules/application/hooks/useSteps";
import getApplicationProcessSteps from "@modules/application/lib/applicationProcess";
import { ApplicationStepId } from "@modules/application/lib/applicationProcess/types";
import { useRouter } from "next/router";
import { FC, useEffect, useMemo } from "react";

export type ApplicationProcessTypes = ApplicationFormValues["applicationType"];

interface ApplicationProcessProps {
  applicationType?: ApplicationProcessTypes;
  hasZeroStep: boolean;
  skipRealEstateAgentFee?: boolean;
  isCertificate?: boolean;
  stepId: string;
}

const ApplicationProcess: FC<ApplicationProcessProps> = ({
  stepId,
  applicationType,
  hasZeroStep,
  skipRealEstateAgentFee = false,
  isCertificate = false,
}) => {
  useTrackEvent(FathomEvent.APPLICATION_STARTED);

  const { t } = useApplicationTranslation();
  const { dispatch, state } = useApplicationContext();
  const { partner } = usePartnerContext();
  const router = useRouter();
  const locale = useLocale();

  const steps = useMemo(() => {
    return getApplicationProcessSteps(t, {
      locale,
      partnerSlug: partner?.slug,
      skipRealEstateAgentFee,
      isCertificate,
      applicationType,
    });
  }, [
    applicationType,
    skipRealEstateAgentFee,
    locale,
    t,
    partner?.slug,
    isCertificate,
  ])!;

  const {
    handleNextStep,
    handlePreviousStep,
    nextSteps,
    value,
    numberOfSteps,
    currentStepNumber,
    currentStep,
  } = useSteps({
    stepId,
    steps,
    hasZeroStep,
    formState: state.applicationFormState,
    onInvalidState() {
      router.replace(
        getApplicationProcessPath(
          locale,
          applicationTypeToPath(locale, applicationType),
          undefined,
          partner?.id,
        ),
      );
    },
    onGoBack() {
      router.back();
    },
    onStepChange(step) {
      const path: string[] = [];

      if (isCertificate) {
        path.push("certificate");
      }

      if (step.id === ApplicationStepId.leadWall && state.lead) {
        router.push(
          getApplicationProcessPath(
            locale,
            state.lead.isOffersViewForbidden ? "forbidden" : "offers",
            undefined,
            partner?.id,
          ),
        );
        return;
      }

      if (applicationType && !isCertificate) {
        path.push(applicationTypeToPath(locale, applicationType));
      }

      path.push(step.id);
      router.push(
        getApplicationProcessPath(
          locale,
          path.join("/"),
          undefined,
          partner?.id,
        ),
      );
    },
    onSetForm(form) {
      dispatch({ type: "SET_APPLICATION_FORM_STATE", form });
    },
    onComplete(step) {
      if (step.navigateTo) {
        router.push(step.navigateTo);
      }
    },
  });

  useEffect(() => {
    if (
      !applicationType ||
      state.applicationFormState.values[ApplicationStepId.applicationType]
    ) {
      return;
    }
    if (applicationType) {
      dispatch({
        type: "SET_APPLICATION_FORM_VALUE",
        stepId: ApplicationStepId.applicationType,
        value: { applicationType },
      });
    }
  }, [applicationType, dispatch, state.applicationFormState]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    dispatch({
      type: "DELETE_APPLICATION",
    });
  }, [dispatch]);

  const Component = currentStep.component || FormScreen;

  return (
    <Component
      key={currentStep.id}
      steps={nextSteps}
      onPrevious={handlePreviousStep}
      onNext={handleNextStep}
      numberOfSteps={numberOfSteps}
      hasZeroStep={hasZeroStep}
      currentStepNumber={currentStepNumber}
      step={currentStep}
      value={value}
    />
  );
};

export default ApplicationProcess;
